import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

// Lecteur audio RAV : un seul bouton pause/lecture + une grande barre tactile
// qu'on touche ou fait glisser du doigt pour se déplacer dans la séance.
// Le parent pilote la lecture via la prop `playing` ; onToggle prévient le parent.
export default function AudioPlayer({ src, playing, onToggle, onEnded, loop = false, testId = "rav-player" }) {
    const [audio] = useState(() => {
        const a = new Audio();
        a.preload = "auto";
        return a;
    });
    const [time, setTime] = useState(0);
    const [dur, setDur] = useState(0);
    const trackRef = useRef(null);
    const dragging = useRef(false);

    // Nouvelle source → on recharge et on repart de zéro
    useEffect(() => {
        audio.src = src;
        window.__ravPlayerSrc = src; // témoin de vérification
        audio.loop = loop;
        setTime(0);
        setDur(0);
        if (playing) audio.play().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, loop]);

    // Pilotage lecture/pause depuis le parent
    useEffect(() => {
        if (playing) audio.play().catch(() => {});
        else audio.pause();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing, src]);

    // Progression
    useEffect(() => {
        const onTime = () => setTime(audio.currentTime);
        const onMeta = () => setDur(audio.duration);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        return () => {
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
        };
    }, [audio]);

    // Fin de lecture (voix guidée terminée avant la fin de l'étape, par ex.)
    useEffect(() => {
        const onEnd = () => onEnded?.();
        audio.addEventListener("ended", onEnd);
        return () => audio.removeEventListener("ended", onEnd);
    }, [audio, onEnded]);

    // Nettoyage au démontage
    useEffect(() => () => audio.pause(), [audio]);

    // Témoin d'état (vérification navigateur)
    useEffect(() => {
        const onPlay = () => { window.__ravPlayerPaused = false; };
        const onPause = () => { window.__ravPlayerPaused = true; };
        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
        };
    }, [audio]);

    // Pause/reprise : on agit DIRECTEMENT dans le geste (règle iOS — sinon le
    // redémarrage après pause peut être refusé), puis on prévient le parent.
    const handleToggle = () => {
        if (audio.paused) audio.play().catch(() => {});
        else audio.pause();
        onToggle?.();
    };

    const seekToClientX = (clientX) => {
        if (!Number.isFinite(audio.duration) || !trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        try {
            if (ratio >= 0.97) {
                // Curseur mené au bout : le contenu est considéré comme terminé,
                // l'événement « ended » enchaîne sur la suite (jamais de redémarrage)
                audio.currentTime = audio.duration;
                return;
            }
            audio.currentTime = ratio * audio.duration;
            setTime(audio.currentTime);
        } catch {
            // Flux pas encore cherchable : on ignore, la lecture continue
        }
    };

    const onPointerDown = (e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        seekToClientX(e.clientX);
    };
    const onPointerMove = (e) => {
        if (dragging.current) seekToClientX(e.clientX);
    };
    const onPointerUp = () => {
        dragging.current = false;
    };

    const pct = dur ? Math.min(100, (time / dur) * 100) : 0;

    return (
        <div className="rav-player" data-testid={testId}>
            <div className="rav-player-controls">
                <button type="button" className="rav-player-btn rav-player-toggle" data-testid={`${testId}-toggle`} aria-label={playing ? "Mettre en pause" : "Reprendre"} onClick={handleToggle}>
                    {playing ? <Pause size={19} strokeWidth={1.8} /> : <Play size={19} strokeWidth={1.8} />}
                </button>
            </div>
            <div className="rav-player-trackrow">
                <span className="rav-player-time" data-testid={`${testId}-time-current`}>{fmt(time)}</span>
                <div
                    ref={trackRef}
                    className="rav-player-track"
                    data-testid={`${testId}-progress`}
                    role="slider"
                    aria-label="Progression de l'audio"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(dur)}
                    aria-valuenow={Math.round(time)}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                >
                    <div className="rav-player-fill" style={{ width: `${pct}%` }} />
                    <div className="rav-player-thumb" style={{ left: `${pct}%` }} />
                </div>
                <span className="rav-player-time" data-testid={`${testId}-time-total`}>{fmt(dur)}</span>
            </div>
        </div>
    );
}
